import React, { useEffect } from "react";
import "./App.css";

function App() {
  /* ------------ SCROLL REVEAL FOR PROJECT CARDS ------------ */
  useEffect(() => {
    const cards = document.querySelectorAll(".project-card-vertical");

    if (!cards.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("scroll-visible");
            obs.unobserve(entry.target); // reveal only once
          }
        });
      },
      {
        threshold: 0.25, // 25% of card visible
      }
    );

    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="page">
      {/* ================= HERO ================= */}
      <header className="hero full-screen" id="home">
        <div className="top-nav">
          <div className="nav-logo">
            <span className="nav-logo-main">ResQpoint VR</span>
            <span className="nav-logo-sub">Team Portfolio</span>
          </div>

          <ul className="nav-links">
            <li>
              <a href="#team">Team</a>
            </li>

            <li className="nav-dropdown">
              <span className="nav-link-with-arrow">
                Projects <span className="nav-arrow">▾</span>
              </span>

              <ul className="dropdown-menu">
                <li>
                  <a href="#project-1">ResQpoint VR</a>
                </li>

                {/* Twinmotion links to separate investor page */}
                <li>
                  <a href="/twinmotion.html">Twinmotion – Portfolio</a>
                </li>

                <li>
                  <span className="dropdown-item-muted">
                    Unreal Cinematic – Coming Soon
                  </span>
                </li>
              </ul>
            </li>

            <li>
              <a href="#video">Demo</a>
            </li>
          </ul>
        </div>

        <div className="hero-content">
          <p className="eyebrow">
            Team Portfolio • Algoma University • Fall 2025
          </p>
          <h1>ResQpoint VR</h1>
          <p className="subtitle">
            Virtual reality first-aid training simulator that makes CPR and AED
            learning affordable, repeatable, and accessible.
          </p>

          <div className="hero-buttons">
            <a href="#team">View Team</a>
            <a href="#project-1" className="secondary">
              Project Details
            </a>
          </div>
        </div>

        <div className="scroll-hint">Scroll to explore ↓</div>
      </header>

      {/* ================= MAIN ================= */}
      <main>
        {/* TEAM */}
        <section id="team" className="section-full">
          <div className="section-inner">
            <h2>Our Team</h2>
            <p>Meet the members who contributed to this project.</p>

            <div className="team-grid">
              <TeamCard
                name="Ramesh Mistry"
                role="Lead & Developer"
                detail="Leads VR experience design, overall vision, and coordination across the team."
                image="/Ramesh_TL.jpg"
                linkedin="https://www.linkedin.com/in/ramesh-mistry"
              />

              <TeamCard
                name="Vivek Patel"
                role="Coordinator"
                detail="Coordinates development tasks, connects features together, and supports sprint planning."
                image="/Vivek.jpg"
                linkedin="https://www.linkedin.com/in/vivekpatel6298"
              />

              <TeamCard
                name="Bhargav Patel"
                role="Project Manager"
                detail="Supports planning, scope tracking, and documentation to keep milestones on schedule."
                image="/Bhargav.jpg"
                linkedin="http://www.linkedin.com/in/bhargav-patel-10580711a"
              />

              <TeamCard
                name="Parth Patel"
                role="UI/UX Designer"
                detail="Designs user flows, interface layouts, and visual style to keep the simulator clear and engaging."
                image="/Parth.jpg"
                linkedin="https://www.linkedin.com/in/parth-patel-9456aa160"
              />

              <TeamCard
                name="Ravirajsinh Thakor"
                role="Quality Tester"
                detail="Tests builds, reports issues, and helps ensure a stable and smooth training experience."
                image="/Ravi.jpg"
                linkedin="https://www.linkedin.com/in/ramesh-mistry"
              />
            </div>
          </div>
        </section>

        {/* PROJECT DETAILS 1 */}
        <section id="project-1" className="project-screen alt-bg">
          <div className="section-inner">
            <h2>Project Details — ResQpoint VR</h2>
            <p className="project-intro">
              ResQpoint VR transforms traditional first-aid education into an
              immersive VR experience. These are the core training capabilities.
            </p>

            <div className="project-card-row">
              <div className="project-card-vertical">
                <span className="project-tag dark">Core Module</span>
                <img
                  src="/Training.png"
                  alt="Immersive VR Training"
                  className="project-img"
                />
                <h3>Immersive VR Training</h3>
                <p>
                  Guided and unguided modules teach CPR, AED usage, and
                  essential first-aid techniques inside a realistic 3D
                  environment.
                </p>
              </div>

              <div className="project-card-vertical">
                <span className="project-tag dark">Skill Tracking</span>
                <img
                  src="/performance.png?v=3"
                  alt="Performance Feedback"
                  className="project-img"
                />
                <h3>Performance Feedback</h3>
                <p>
                  Users receive real-time scoring and feedback to understand
                  accuracy, timing, and overall skill level. Trainers can review
                  progress over time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* PROJECT DETAILS 2 */}
        <section id="project-2" className="project-screen">
          <div className="section-inner">
            <div className="project-card-row">
              <div className="project-card-vertical">
                <span className="project-tag dark">Engagement</span>
                <img
                  src="/quiz1.png"
                  alt="Gamified Learning"
                  className="project-img"
                />
                <h3>Gamified Learning</h3>
                <p>
                  Quizzes, challenge levels, and simulation tasks keep learners
                  engaged.
                </p>
              </div>

              <div className="project-card-vertical">
                <span className="project-tag dark">Value</span>
                <img
                  src="/many.png"
                  alt="Accessible & Repeatable"
                  className="project-img"
                />
                <h3>Accessible & Repeatable</h3>
                <p>
                  Unlimited practice without scheduling barriers — ideal for
                  schools and workplaces.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* VIDEO – text left, video right on dark navy */}
        <section id="video" className="video-section">
          <div className="video-inner">
            {/* left side text */}
            <div className="video-text">
              <span className="video-label">LIVE DEMO • 2-MINUTE OVERVIEW</span>
              <h2>Project Demo Video</h2>

              <p className="video-description">
                See how ResQpoint VR guides learners through CPR, AED usage, and
                emergency response in an immersive training room.
              </p>

              <ul className="video-points">
                <li>Shows core CPR training flow inside the VR environment.</li>
                <li>
                  Highlights real-time feedback, scoring, and performance
                  tracking.
                </li>
                <li>Best experienced with sound on and full screen.</li>
              </ul>

              <p className="video-tip">
                <span>Tip:</span> Use HD and full screen for best viewing in
                class.
              </p>
            </div>

            {/* right side video frame */}
            <div className="video-frame-shell">
              <div className="video-frame-header">
                <span className="window-dot dot-red" />
                <span className="window-dot dot-amber" />
                <span className="window-dot dot-green" />
                <span className="video-frame-title">ResQpoint VR — Demo</span>
              </div>

              <div className="video-frame-body">
                <div className="video-wrapper">
                  <iframe
                    src="https://www.youtube.com/embed/eY7fOe5MCoE"
                    title="ResQpoint VR Demo"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        © 2025 ResQpoint VR Team • Contact: rameshmistry1096@gmail.com
      </footer>
    </div>
  );
}

/* TEAM CARD COMPONENT */
function TeamCard({ name, role, image, linkedin, detail }) {
  return (
    <div className="card">
      <div className="team-photo-wrapper">
        <img src={image} alt={name} className="team-photo-rect" />
        <div className="role-ribbon">{role.toUpperCase()}</div>
      </div>

      <h3>{name}</h3>
      <p className="team-role-caption">{detail}</p>

      <a
        className="linkedin-btn"
        href={linkedin}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span className="linkedin-icon">in</span>
        <span>LinkedIn Profile</span>
      </a>
    </div>
  );
}

export default App;
